/**
 * オプションの型・マージ・宣言的属性の解釈（FR-INIT）。
 *
 * マージ順（後勝ち）: ライブラリ既定 → グローバル既定 → create() 引数 → data 属性。
 */
import { DATA_PREFIX } from '../brand';
import type { DeliveryOption } from '../source/delivery';
import { LIBRARY_DEFAULTS, defaults } from './defaults';
import type { EventHandlers } from './events';

export type ReadingDirection = 'ltr' | 'rtl';
/** 綴じ方向の指定。auto はコンテンツ（マニフェスト）に追従する。 */
export type DirectionOption = 'auto' | ReadingDirection;
export type ViewMode = 'single' | 'spread' | 'auto';
export type EngineKind = 'auto' | 'css2d' | 'webgl3d';
export type FlipDirection = 'forward' | 'back';

const DIRECTIONS: readonly DirectionOption[] = ['auto', 'ltr', 'rtl'];
const VIEW_MODES: readonly ViewMode[] = ['single', 'spread', 'auto'];
const ENGINE_KINDS: readonly EngineKind[] = ['auto', 'css2d', 'webgl3d'];
const DELIVERY_OPTIONS: readonly DeliveryOption[] = ['auto', 'pdf', 'image'];

/** UI 構成（表示するパネル・ツールバー）。 */
export interface UiOptions {
  toolbar: boolean;
  thumbnails: boolean;
  outline: boolean;
  /** 検索パネル（既定 ON・索引のあるブックでのみ表示・FR-SEARCH）。 */
  search: boolean;
  /** 共有パネル（既定 ON・FR-LINK-03〜05）。 */
  share: boolean;
  /** 目印（しおり・ふせん・本棚）のボタンと一覧パネル（既定 ON・EP-1）。 */
  marks: boolean;
  /** 公開コメントのパネルといいねボタン（既定 ON・EP-1 公開データ）。 */
  comments: boolean;
  /** 操作ヘルプのパネルとボタン（既定 ON・`?` キーでも開く）。 */
  help: boolean;
  /**
   * 誌面の読み上げのパネルとボタン（既定 ON・M11）。ブラウザが `SpeechSynthesis` を
   * 持たない環境では、この指定によらず出さない（口が無いのに操作子だけ出さない）。
   * **読み上げは利用者の操作からしか始まらない**（自動再生しない）。
   */
  speech: boolean;
  /**
   * 原本 PDF のダウンロードボタン（既定 ON・FR-DL-01）。PDF 配信モードでのみ出る。
   * PDF 自身が内容の抽出を禁じている場合は、この指定によらず出さない（FR-DL-02）。
   */
  download: boolean;
  /**
   * 印刷ボタン（既定 ON・FR-PRINT-01）。PDF 配信モードでのみ出る。
   * PDF 自身が印刷を禁じている場合は、この指定によらず出さない。
   */
  print: boolean;
}

/** 共有先（SNS 等）。url は {url} / {title} を含む URL テンプレート。 */
export interface ShareTarget {
  id: string;
  label: string;
  url: string;
}

/** 共有 UI の設定（FR-LINK-04・FR-LINK-05）。 */
export interface ShareOptions {
  /**
   * 共有先テンプレート。既定は空 — 要件が「既定セットに依存しない」と定めているため、
   * 特定サービスを組み込みで持たない。
   */
  targets: ShareTarget[];
  /**
   * 埋め込みコードに出してよい配信元。空 = 制限しない。
   * 非空で現在の origin が含まれないときは、先頭の要素へ書き換える（正規化表）。
   */
  embedAllowlist: string[];
  /** 埋め込み先の基準 URL。空なら共有 URL（正規 OGP ページ）から導く。 */
  embedBase: string;
  embedWidth: string;
  embedHeight: string;
  /**
   * 正規 OGP ページ（/view/{slug}）の基準（origin+任意の prefix）。空なら source から
   * 導出する（M5b-2）。共有 URL・QR・SNS・埋め込みの既定先はこのページを指す。
   */
  viewBase: string;
  /** QR コードを表示するか。 */
  qr: boolean;
}

/**
 * 閲覧イベントの収集と送信（EP-6）。
 *
 * 自前収集は既定 ON（同一サーバへ送るだけで第三者提供はしない）。GA4 / GTM への
 * 外部送信は**導入者が明示的に有効化したときだけ**動く — 何がどこへ送られるかは
 * 同梱の「外部送信情報一覧」に書き出してある。
 */
export interface AnalyticsOptions {
  /** 自前の収集エンドポイント（POST /api/v1/events）へ送る。 */
  collect: boolean;
  /** ホストページの gtag() へ流す（存在しなければ何もしない）。 */
  gtag: boolean;
  /** ホストページの dataLayer へ push する（GTM 経由）。 */
  dataLayer: boolean;
}

/** 言語設定。strings は組み込み辞書への差分。 */
export interface I18nOptions {
  /** 'auto' で document.documentElement.lang → navigator の順に解決する。 */
  locale: string;
  strings: Record<string, string>;
}

/** テーマ上書き。キーは接頭辞を除いた名前（bg → CSS カスタムプロパティの bg）。 */
export type ThemeOptions = Record<string, string>;

/** 配色プリセット。auto は OS/ブラウザの設定に従う。 */
export type ThemePreset = 'auto' | 'dark' | 'light';

const THEME_PRESETS: readonly ThemePreset[] = ['auto', 'dark', 'light'];

export interface ViewerOptions {
  /** manifest の URL。 */
  source: string;
  /** 側面資産（worker・cmaps 等）の基準 URL。空ならスクリプト自身の位置から解決する。 */
  assetBase: string;
  /** 初期ページ（1 始まり）。 */
  page: number;
  direction: DirectionOption;
  mode: ViewMode;
  engine: EngineKind;
  /**
   * 配信モードの上書き（既定 auto = マニフェスト追従・FR-DELIV-02）。
   * ここでいうモードは PDF 配信 / 画像配信の別であり、単/見開きの mode とは別物。
   */
  delivery: DeliveryOption;
  hardcover: boolean;
  zoomMax: number;
  zoomStep: number;
  /** ハッシュナビゲーション（既定 ON・FR-LINK-01）。 */
  hash: boolean;
  /** ページめくり音（既定 ON・FR-SND-01）。 */
  sound: boolean;
  /** めくり音の差替え音源 URL。空なら Web Audio で合成する。 */
  soundFile: string;
  /** 検索の最低文字数（既定 2・FR-SEARCH-05）。これ未満は問い合わせない。 */
  searchMinChars: number;
  /**
   * PDF を Range で取りにいくときのチャンク長（バイト・既定 65536・FR-SRC-02）。
   * サーバやプロキシが Range を落とす経路では、自動的に全体取得へ切り替わる。
   */
  rangeChunkSize: number;
  /**
   * ユーザーデータ（しおり・ふせん・本棚・再開位置・履歴）の保存（既定 ON・EP-1）。
   * 未ログインのときは端末の localStorage に保存し、ログインするとサーバへ引き継ぐ。
   * false にすると端末にもサーバにも一切保存しない。
   */
  userData: boolean;
  /**
   * 誌面オーバーレイ（リンク・動画・音声・フォーム — 既定 ON・EP-2）。
   * false にするとオーバーレイ定義を取りに行かず、誌面には何も重ねない。
   */
  overlays: boolean;
  /** 閲覧イベントの収集（EP-6）。自前収集は既定 ON・外部送信は明示オプトイン。 */
  analytics: AnalyticsOptions;
  share: ShareOptions;
  ui: UiOptions;
  preset: ThemePreset;
  theme: ThemeOptions;
  i18n: I18nOptions;
  /** オプション経由のイベントハンドラ（on() と併用可・FR-API-04）。 */
  on: Partial<EventHandlers>;
}

/** create() やグローバル既定に渡せる部分形。グループは 1 段だけ部分指定できる。 */
export type ViewerOptionsInput = Partial<
  Omit<ViewerOptions, 'ui' | 'i18n' | 'share' | 'analytics'>
> & {
  ui?: Partial<UiOptions>;
  i18n?: Partial<I18nOptions>;
  share?: Partial<ShareOptions>;
  analytics?: Partial<AnalyticsOptions>;
};

/** 1 段だけ浅くマージするグループキー。 */
const GROUP_KEYS = ['ui', 'i18n', 'theme', 'on', 'share', 'analytics'] as const;
type GroupKey = (typeof GROUP_KEYS)[number];

const isGroupKey = (key: string): key is GroupKey =>
  (GROUP_KEYS as readonly string[]).includes(key);

/**
 * 層を順に重ねて確定オプションを得る（後の層が勝つ）。
 * グループ（ui/i18n/theme/on/share/analytics）はキー単位で上書き、それ以外は値ごと上書きする。
 * 配列（share.targets・share.embedAllowlist）は要素単位ではなく丸ごと置換する。
 */
export function mergeOptions(
  base: ViewerOptions,
  ...layers: readonly ViewerOptionsInput[]
): ViewerOptions {
  const result: ViewerOptions = {
    ...base,
    ui: { ...base.ui },
    i18n: { ...base.i18n, strings: { ...base.i18n.strings } },
    theme: { ...base.theme },
    on: { ...base.on },
    analytics: { ...base.analytics },
    // 配列まで複製する。浅い複製だけだと targets / embedAllowlist が
    // LIBRARY_DEFAULTS と同一実体のままになり、1 台への push が全ビューワへ漏れる。
    share: {
      ...base.share,
      targets: [...base.share.targets],
      embedAllowlist: [...base.share.embedAllowlist],
    },
  };
  for (const layer of layers) {
    for (const [key, value] of Object.entries(layer)) {
      if (value === undefined) continue;
      if (isGroupKey(key) && typeof value === 'object' && value !== null) {
        Object.assign(result[key], value);
      } else {
        Reflect.set(result, key, value);
      }
    }
  }
  return result;
}

const asBool = (raw: string): boolean => raw !== 'false' && raw !== '0' && raw !== 'off';

const asInt = (raw: string): number | undefined => {
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : undefined;
};

const asNum = (raw: string): number | undefined => {
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : undefined;
};

/** カンマ区切りの一覧（空要素は落とす）。 */
const asList = (raw: string): string[] =>
  raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');

const asEnum = <T extends string>(values: readonly T[], raw: string): T | undefined =>
  (values as readonly string[]).includes(raw) ? (raw as T) : undefined;

interface AttrSpec {
  /** data 属性名から接頭辞を除いた部分（接頭辞 + 'zoom-max' → zoomMax オプション）。 */
  readonly suffix: string;
  readonly apply: (target: ViewerOptionsInput, raw: string) => void;
}

/** 解釈する data 属性の一覧（明示表・推測しない）。 */
const ATTRS: readonly AttrSpec[] = [
  { suffix: 'source', apply: (o, raw) => { o.source = raw; } },
  { suffix: 'asset-base', apply: (o, raw) => { o.assetBase = raw; } },
  { suffix: 'page', apply: (o, raw) => { const v = asInt(raw); if (v !== undefined) o.page = v; } },
  { suffix: 'direction', apply: (o, raw) => { const v = asEnum(DIRECTIONS, raw); if (v) o.direction = v; } },
  { suffix: 'mode', apply: (o, raw) => { const v = asEnum(VIEW_MODES, raw); if (v) o.mode = v; } },
  { suffix: 'engine', apply: (o, raw) => { const v = asEnum(ENGINE_KINDS, raw); if (v) o.engine = v; } },
  { suffix: 'delivery', apply: (o, raw) => { const v = asEnum(DELIVERY_OPTIONS, raw); if (v) o.delivery = v; } },
  { suffix: 'hardcover', apply: (o, raw) => { o.hardcover = asBool(raw); } },
  { suffix: 'zoom-max', apply: (o, raw) => { const v = asNum(raw); if (v !== undefined) o.zoomMax = v; } },
  { suffix: 'zoom-step', apply: (o, raw) => { const v = asNum(raw); if (v !== undefined) o.zoomStep = v; } },
  { suffix: 'hash', apply: (o, raw) => { o.hash = asBool(raw); } },
  { suffix: 'sound', apply: (o, raw) => { o.sound = asBool(raw); } },
  { suffix: 'sound-file', apply: (o, raw) => { o.soundFile = raw; } },
  { suffix: 'search-min-chars', apply: (o, raw) => { const v = asInt(raw); if (v !== undefined) o.searchMinChars = v; } },
  { suffix: 'range-chunk-size', apply: (o, raw) => { const v = asInt(raw); if (v !== undefined) o.rangeChunkSize = v; } },
  { suffix: 'user-data', apply: (o, raw) => { o.userData = asBool(raw); } },
  { suffix: 'overlays', apply: (o, raw) => { o.overlays = asBool(raw); } },
  { suffix: 'analytics', apply: (o, raw) => { (o.analytics ??= {}).collect = asBool(raw); } },
  { suffix: 'analytics-gtag', apply: (o, raw) => { (o.analytics ??= {}).gtag = asBool(raw); } },
  { suffix: 'analytics-data-layer', apply: (o, raw) => { (o.analytics ??= {}).dataLayer = asBool(raw); } },
  { suffix: 'preset', apply: (o, raw) => { const v = asEnum(THEME_PRESETS, raw); if (v) o.preset = v; } },
  { suffix: 'toolbar', apply: (o, raw) => { (o.ui ??= {}).toolbar = asBool(raw); } },
  { suffix: 'thumbnails', apply: (o, raw) => { (o.ui ??= {}).thumbnails = asBool(raw); } },
  { suffix: 'outline', apply: (o, raw) => { (o.ui ??= {}).outline = asBool(raw); } },
  { suffix: 'search', apply: (o, raw) => { (o.ui ??= {}).search = asBool(raw); } },
  { suffix: 'share', apply: (o, raw) => { (o.ui ??= {}).share = asBool(raw); } },
  { suffix: 'marks', apply: (o, raw) => { (o.ui ??= {}).marks = asBool(raw); } },
  { suffix: 'comments', apply: (o, raw) => { (o.ui ??= {}).comments = asBool(raw); } },
  { suffix: 'help', apply: (o, raw) => { (o.ui ??= {}).help = asBool(raw); } },
  { suffix: 'speech', apply: (o, raw) => { (o.ui ??= {}).speech = asBool(raw); } },
  { suffix: 'download', apply: (o, raw) => { (o.ui ??= {}).download = asBool(raw); } },
  { suffix: 'print', apply: (o, raw) => { (o.ui ??= {}).print = asBool(raw); } },
  { suffix: 'qr', apply: (o, raw) => { (o.share ??= {}).qr = asBool(raw); } },
  { suffix: 'embed-base', apply: (o, raw) => { (o.share ??= {}).embedBase = raw; } },
  { suffix: 'view-base', apply: (o, raw) => { (o.share ??= {}).viewBase = raw; } },
  { suffix: 'embed-width', apply: (o, raw) => { (o.share ??= {}).embedWidth = raw; } },
  { suffix: 'embed-height', apply: (o, raw) => { (o.share ??= {}).embedHeight = raw; } },
  { suffix: 'embed-allowlist', apply: (o, raw) => { (o.share ??= {}).embedAllowlist = asList(raw); } },
  { suffix: 'locale', apply: (o, raw) => { (o.i18n ??= {}).locale = raw; } },
];

/** 要素の data 属性を読み、部分オプションへ変換する（マージ最上位層）。 */
export function readDataOptions(el: Element): ViewerOptionsInput {
  const options: ViewerOptionsInput = {};
  for (const spec of ATTRS) {
    const raw = el.getAttribute(`${DATA_PREFIX}${spec.suffix}`);
    if (raw === null) continue;
    spec.apply(options, raw);
  }
  return options;
}

/**
 * 確定オプションを得る（マージ順の唯一の実装）。
 * ライブラリ既定 → グローバル既定 → create() 引数 → data 属性。
 */
export function resolveOptions(
  el: Element,
  args: ViewerOptionsInput = {},
  globals: ViewerOptionsInput = defaults,
): ViewerOptions {
  const options = mergeOptions(LIBRARY_DEFAULTS, globals, args, readDataOptions(el));
  if (!options.source) {
    throw new Error(
      `ビューワの source が指定されていない（${DATA_PREFIX}source 属性か create({ source }) が必要）`,
    );
  }
  return options;
}
